import React, { useEffect, useState, useRef, useCallback } from "react";
import { FaList } from "react-icons/fa";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import io from "socket.io-client";
import {
  add_friend,
  send_message,
  send_file_message,
  updateMessage,
  messageClear,
} from "../../store/reducers/chatReducer";
import toast from "react-hot-toast";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import {
  MessageSquareText,
  Send,
  Check,
  CheckCheck,
  MoreHorizontal,
  AlertTriangle,
  Paperclip,
  Image,
  File,
  X,
  Download,
  FileText,
  Eye,
  Video,
  Play,
} from "lucide-react";
import axios from "../../api/api";

const socket = io("http://localhost:5000");

const GEMINI_API_KEY = "AIzaSyB7z1zuFpGgmjW0p8cw6GeJCWaYQqeiw8s";
const GEMINI_API_URL =
  "https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent";

const MESSAGE_TYPES = {
  PRODUCT_INQUIRY: "product_inquiry",
  SHIPPING_STATUS: "shipping_status",
  RETURN_REFUND: "return_refund",
  TECHNICAL_ISSUE: "technical_issue",
  GENERAL_QUERY: "general_query",
  URGENT_SUPPORT: "urgent_support",
};

const RESPONSE_TEMPLATES = {
  [MESSAGE_TYPES.PRODUCT_INQUIRY]: {
    template: "I can help you with information about our products. {response}",
    followUp:
      "Would you like to know more about specific product features or pricing?",
  },
  [MESSAGE_TYPES.SHIPPING_STATUS]: {
    template: "Regarding your shipping inquiry: {response}",
    followUp:
      "Is there anything specific about the shipping you'd like to know?",
  },
  [MESSAGE_TYPES.RETURN_REFUND]: {
    template: "Let me assist you with your return/refund request. {response}",
    followUp: "Would you like me to explain our return policy in detail?",
  },
};

const Chat = () => {
  const [isAIEnabled, setIsAIEnabled] = useState(true);
  const [aiContext, setAIContext] = useState("");
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [aiConversationHistory, setAiConversationHistory] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [storeData, setStoreData] = useState(null);
  const [needsHumanAttention, setNeedsHumanAttention] = useState(false);
  const messageContainerRef = useRef();
  const audioRef = useRef(new Audio("/sounds/notification.mp3"));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sellerId } = useParams();
  const [text, setText] = useState("");
  const [receverMessage, setReceverMessage] = useState("");
  const [activeSeller, setActiveSeller] = useState([]);
  const { userInfo } = useSelector((state) => state.auth);
  const { fd_messages, currentFd, my_friends, successMessage } = useSelector(
    (state) => state.chat
  );
  const [show, setShowLeftSidebar] = useState(false);

  const [customPrompts, setCustomPrompts] = useState([]);
  const [autoForwardRules, setAutoForwardRules] = useState({
    complexQueries: true,
    highValueCustomers: true,
    afterHours: false,
  });

  // State for message menu options and deleted messages
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [deletedMessages, setDeletedMessages] = useState(new Set());
  
  // File upload states
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Image fullscreen states
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);
  const loadStoreData = async (sellerId) => {
    try {
      const response = await axios.get(`/home/store/training-data/${sellerId}`);
      setStoreData(response.data);
      // Update AI context with store-specific information
      const storeContext = `
        This is the Omah Global e-commerce platform.
        Store: ${response.data.storeName}
        products: ${response.data.products}
      `;
      setAIContext((prevContext) => `${storeContext}\n${prevContext}`);
    } catch (error) {
      console.error("Error loading store data:", error);
    }
  };

  const classifyMessage = async (message) => {
    try {
      const response = await fetch(`${GEMINI_API_URL}?key=${GEMINI_API_KEY}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          contents: [
            {
              parts: [
                {
                  text: `Classify the following customer message into one of these categories: ${Object.values(
                    MESSAGE_TYPES
                  ).join(", ")}. Message: ${message}`,
                },
              ],
            },
          ],
        }),
      });

      const data = await response.json();
      return data.candidates[0].content.parts[0].text.toLowerCase();
    } catch (error) {
      console.error("Classification error:", error);
      return MESSAGE_TYPES.GENERAL_QUERY;
    }
  };
  const getGeminiResponse = async (message, context) => {
    setIsTyping(true);
    try {
      // Classify message
      const messageType = await classifyMessage(message);

      // Prepare enhanced context
      const enhancedContext = `
        ${context}
        Store Information:
        ${JSON.stringify(storeData)}
        
        Message Type: ${messageType}
        
        Instructions:
        - You are a helpful AI assistant for the Omah Global e-commerce platform
        - Provide concise, accurate responses
        - If you can't help, politely indicate that a human representative will follow up
        - Don't make up information about products or policies
        - Use the store's actual data for responses
      `;

      const response = await fetch(`${GEMINI_API_URL}?key=${GEMINI_API_KEY}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          contents: [
            {
              parts: [
                {
                  text: `${enhancedContext}\n\nConversation History:\n${aiConversationHistory
                    .map((msg) => `${msg.role}: ${msg.content}`)
                    .join("\n")}\n\nCustomer: ${message}\n\nAssistant:`,
                },
              ],
            },
          ],
          generationConfig: {
            temperature: 0.7,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 1024,
          },
        }),
      });

      const data = await response.json();
      let aiResponse = data.candidates[0].content.parts[0].text;

      // Apply response template if available
      if (RESPONSE_TEMPLATES[messageType]) {
        aiResponse = RESPONSE_TEMPLATES[messageType].template.replace(
          "{response}",
          aiResponse
        );
      }

      // Check if human attention is needed
      const needsHuman = checkIfNeedsHumanAttention(message, messageType);
      setNeedsHumanAttention(needsHuman);

      return aiResponse;
    } catch (error) {
      console.error("Gemini API Error:", error);
      throw new Error("Failed to get AI response");
    } finally {
      setIsTyping(false);
    }
  };

  const checkIfNeedsHumanAttention = (message, messageType) => {
    const triggers = [
      message.toLowerCase().includes("speak to human"),
      message.toLowerCase().includes("urgent"),
      messageType === MESSAGE_TYPES.URGENT_SUPPORT,
      message.length > 200, // Complex queries
      message.toLowerCase().includes("refund") &&
        message.toLowerCase().includes("immediate"),
    ];

    return triggers.some((trigger) => trigger === true);
  };

  useEffect(() => {
    if (!sellerId && my_friends.length > 0) {
      navigate(`/dashboard/chat/${my_friends[0].fdId}`);
    }
    loadStoreData(sellerId);
  }, [my_friends, sellerId, navigate]);

  const scrollToBottom = () => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop =
        messageContainerRef.current.scrollHeight;
    }
  };

  const playNotification = () => {
    audioRef.current.currentTime = 0;
    audioRef.current
      .play()
      .catch((err) => console.log("Audio playback failed:", err));
  };

  useEffect(() => {
    socket.emit("add_user", userInfo.id, userInfo);
  }, [userInfo]);

  useEffect(() => {
    dispatch(
      add_friend({
        sellerId: sellerId || "",
        userId: userInfo.id,
      })
    );
  }, [sellerId, dispatch, userInfo.id]);

  useEffect(() => {
    if (sellerId) {
      // Mark messages as seen
      axios
        .post(
          "/chat/customer/mark-seen",
          { sellerId, customerId: userInfo.id },
          { withCredentials: true }
        )
        .catch((error) =>
          console.error("Error marking customer messages as seen:", error)
        );
    }
  }, [sellerId, userInfo.id]);

  const checkAIStatus = async () => {
    try {
      const res = await axios.post("/home/isai", { sellerId });
      return res.data.isai; // Return the AI status (true/false)
    } catch (error) {
      console.error("Error checking AI status:", error);
      return false; // Default to false if the request fails
    }
  };

  const send = async () => {
    if (text) {
      // Check if AI is enabled for this seller
      const isAIEnabledForSeller = await checkAIStatus();

      // Dispatch the user's message
      dispatch(
        send_message({
          userId: userInfo.id,
          text,
          sellerId,
          name: userInfo.name,
        })
      );

      setText("");
      playNotification();

      // If AI is enabled, get and display the AI's response
      if (isAIEnabledForSeller) {
        try {
          const aiResponse = await getGeminiResponse(text, aiContext);
          dispatch(
            send_message({
              userId: sellerId,
              text: aiResponse,
              sellerId: userInfo.id,
              name: "AI assistant",
            })
          );
        } catch (error) {
          toast.error("Error fetching AI response");
        }
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      send();
    }
  };

  // File handling functions
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setSelectedFiles(files);
      handleFileUpload(files);
    }
  };

  const handleFileUpload = async (files) => {
    if (!currentFd) {
      toast.error("Please select a seller first");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });
      formData.append('userId', userInfo.id);
      formData.append('sellerId', sellerId);
      formData.append('name', userInfo.name);
      formData.append('message', text || '');

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 100);

      await dispatch(send_file_message(formData)).unwrap();
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      setText('');
      setSelectedFiles([]);
      playNotification();
      
      setTimeout(() => {
        setUploadProgress(0);
        setIsUploading(false);
      }, 1000);
    } catch (error) {
      setIsUploading(false);
      setUploadProgress(0);
      toast.error("Error uploading files");
    }
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Image fullscreen functions
  const openImageFullscreen = (imageUrl) => {
    setFullscreenImage(imageUrl);
    setShowFullscreenModal(true);
  };

  const closeFullscreenModal = () => {
    setShowFullscreenModal(false);
    setFullscreenImage(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return <Image className="w-4 h-4" />;
    if (mimeType?.startsWith('video/')) return <Video className="w-4 h-4" />;
    if (mimeType?.includes('pdf')) return <FileText className="w-4 h-4" />;
    return <File className="w-4 h-4" />;
  };

  const renderMessageContent = (message) => {
    const hasText = message.message && message.message.trim();
    const hasAttachments = message.attachment && message.attachment.length > 0;

    return (
      <div className="space-y-2">
        {hasText && (
          <span className="text-gray-900">{message.message}</span>
        )}
        {hasAttachments && (
          <div className="space-y-2">
            {message.attachment.map((file, index) => (
              <div key={index} className="file-attachment">
                {file.type === 'image' ? (
                  <div className="image-attachment">
                    <img
                      src={file.url}
                      alt="Chat attachment"
                      className="max-w-[280px] max-h-[280px] w-auto h-auto rounded-lg cursor-pointer hover:opacity-95 transition-opacity shadow-lg"
                      onClick={() => openImageFullscreen(file.url)}
                      style={{ objectFit: 'cover' }}
                    />
                  </div>
                ) : file.type === 'video' ? (
                  <div className="video-attachment">
                    <video
                      src={file.url}
                      className="max-w-[280px] max-h-[280px] w-auto h-auto rounded-lg shadow-lg"
                      controls
                      preload="metadata"
                      style={{ objectFit: 'cover' }}
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>
                ) : (
                  <div className="file-preview flex items-center gap-3 p-3 bg-gray-100 rounded-lg max-w-[250px]">
                    {getFileIcon(file.mimeType)}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-900 truncate">{file.originalName}</div>
                      <div className="text-xs text-gray-500">{formatFileSize(file.size)}</div>
                    </div>
                    <button
                      onClick={() => window.open(file.url, '_blank')}
                      className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      const response = await axios.post(
        "/chat/seller/delete-message",
        { messageId },
        { withCredentials: true }
      );
      toast.success(response.data.message || "Message deleted successfully");
      setDeletedMessages((prev) => new Set([...prev, messageId]));
    } catch (error) {
      console.error("Error deleting message:", error);
      toast.error(
        error.response?.data?.message ||
          "An error occurred while deleting the message"
      );
    } finally {
      setOpenMenuIndex(null);
    }
  };

  const handleCopyMessage = (messageText) => {
    navigator.clipboard.writeText(messageText);
    toast.success("Message copied");
    setOpenMenuIndex(null);
  };

  const handleClickOutside = useCallback(
    (event) => {
      if (openMenuIndex !== null) {
        setOpenMenuIndex(null);
      }
    },
    [openMenuIndex]
  );

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    socket.on("seller_message", (msg) => {
      setReceverMessage(msg);
      playNotification();
      toast.success("New message");
    });
    socket.on("activeSeller", (sellers) => {
      setActiveSeller(sellers);
    });
  }, []);

  useEffect(() => {
    if (successMessage) {
      socket.emit("send_customer_message", fd_messages[fd_messages.length - 1]);
      dispatch(messageClear());
      scrollToBottom();
    }
  }, [successMessage, dispatch, fd_messages]);

  useEffect(() => {
    if (receverMessage) {
      if (
        sellerId === receverMessage.senderId &&
        userInfo.id === receverMessage.receverId
      ) {
        dispatch(updateMessage(receverMessage));
        scrollToBottom();
      } else {
        toast.success(`${receverMessage.senderName} sent a message`);
        dispatch(messageClear());
      }
    }
  }, [receverMessage, sellerId, userInfo.id, dispatch]);

  useEffect(() => {
    scrollToBottom();
  }, [fd_messages]);

  if (my_friends.length === 0) {
    return (
      <div className="bg-white border border-gray-100 shadow-sm rounded-md">
        <div className="w-full h-[500px] flex justify-center items-center">
          <div className="flex flex-col items-center gap-4 text-center max-w-sm px-6">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
              <MessageSquareText size={32} className="text-gray-400" />
            </div>
            <div>
              <h3 className="text-gray-900 font-semibold text-lg mb-1">No messages yet</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                You don't have any messages from merchants yet. To start a conversation, visit a merchant's product page and send them a message.
              </p>
            </div>
            <Link
              to="/stores"
              className="mt-1 px-5 py-2 bg-gray-900 text-white text-sm rounded-md hover:bg-gray-700 transition-colors"
            >
              Browse Stores
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-100 shadow-sm rounded-md">
      <div className="justify-center flex max-auto flex relative">
        <div
          className={`w-[230px] z-10 p-5 md-lg:absolute bg-gray-50 transition-all md-lg:h-full ${
            show ? "left-0" : "-left-[350px]"
          }`}
        >
          <div className="flex p-4 gap-3 font-bold items-center text-gray-900 h-[50px]">
            <span>Store messages</span>
            <MessageSquareText size={20} />
          </div>
          <div className="w-full flex flex-col text-gray-700 p-4 h-[400px] pr-3">
            {my_friends.map((f, i) => (
              <Link
                to={`/dashboard/chat/${f.fdId}`}
                key={i}
                className={`flex gap-2 justify-start items-center py-[10px]`}
              >
                <div className="w-[30px] h-[30px] rounded-full relative">
                  <div className="absolute inset-[2px]">
                    <img
                      className="rounded-full w-full h-full"
                      src={`${process.env.REACT_APP_URL}/images/seller.svg`}
                      alt=""
                    />
                  </div>
                  {activeSeller.some((c) => c.sellerId === f.fdId) && (
                    <div className="absolute inset-[-2px] rounded-full border-2 border-green-500"></div>
                  )}
                </div>
                <span className="text-sm">{f.name}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Chat area */}
        <div className="w-[calc(100%-230px)] max-w-[700px] p-5 md-lg:w-full bg-white px-4">
          {currentFd ? (
            <div className="w-full h-full">
              {/* Chat header */}
              <div className="flex justify-between items-center text-slate-600 h-[50px]">
                <div className="flex gap-2 items-center">
                  <div className="w-[30px] h-[30px] rounded-full relative mr-2">
                    {activeSeller.some(
                      (c) => c.sellerId === currentFd.fdId
                    ) && (
                      <div className="absolute inset-[-4px] rounded-full border-2 border-green-500"></div>
                    )}
                    <img
                      className="bml rounded-full w-full h-full"
                      src={`${process.env.REACT_APP_URL}/images/seller.svg`}
                      alt=""
                    />
                  </div>
                  <span className="text-gray-900">{currentFd.name}</span>
                </div>
              </div>

              {/* Messages container */}
              <div className="h-[400px] w-full p-3 rounded-md">
                <div
                  ref={messageContainerRef}
                  className="w-full h-full overflow-y-auto flex flex-col gap-3 scrollbar-hide"
                  style={{
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    "::-webkit-scrollbar": {
                      display: "none",
                    },
                  }}
                >
                  {fd_messages.length === 0 ? (
                    <div className="w-full h-full flex items-center text-sm justify-center text-muted-foreground">
                      <p>You have no shared messages</p>
                    </div>
                  ) : (
                    fd_messages.map((m, i) => {
                      // If message has been deleted, skip it
                      if (deletedMessages.has(m._id)) return null;
                      const timestamp = m.createdAt
                        ? new Date(m.createdAt).toLocaleTimeString()
                        : "";
                      if (currentFd?.fdId !== m.receverId) {
                        // Seller's message (incoming)
                        return (
                            <div key={i} className="flex flex-col items-start mb-3">
                              <div className="flex items-center mb-1">
                                <span className="text-gray-700">{m.senderName}</span>
                          
                                {m.senderName === "AI assistant" && (
                                  <span className="ml-2 bg-green-500/10 rounded-md p-2 text-xs text-green-500">
                                    Automated response
                                  </span>
                                )}
                              </div>
                          
                              <div className="max-w-[70%] bg-gray-100 rounded-2xl rounded-tl-sm px-3 py-2">
                                {renderMessageContent(m)}
                          
                                <div className="flex items-center gap-1 justify-end mt-1">
                                  <span className="text-xs text-gray-400">{timestamp}</span>
                                </div>
                              </div>
                            </div>
                          );
                      } else {
                        // Customer's message (sent) with checkmarks and menu options
                        return (
                          <div
                            key={i}
                            className="flex flex-col items-end mb-3 relative"
                          >
                            <div className="flex flex-col max-w-[70%] bg-[#1e3a2b] rounded-2xl rounded-tr-sm px-3 py-2 relative">
                              {renderMessageContent(m)}
                              <div className="flex items-center gap-1 justify-end mt-1">
                                <span className="text-xs text-gray-400">
                                  {timestamp}
                                </span>
                                {m.status === "seen" ? (
                                  <div className="flex items-center gap-1">
                                    <CheckCheck className="w-4 h-4 text-blue-500" />
                                  </div>
                                ) : (
                                  <Check className="w-4 h-4 text-gray-400" />
                                )}
                                <span
                                  className="cursor-pointer rounded-full p-1"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenuIndex(
                                      openMenuIndex === i ? null : i
                                    );
                                  }}
                                >
                                  <MoreHorizontal className="w-3 h-3 text-gray-400" />
                                </span>
                              </div>
                            </div>
                            {openMenuIndex === i && (
                              <div
                                className="absolute bg-white border border-gray-200 text-gray-700 rounded-lg shadow-lg py-2 right-0 top-full mt-1 z-10 w-24"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  className="block w-full text-left text-sm px-4 py-1"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteMessage(m._id);
                                    setOpenMenuIndex(null);
                                  }}
                                >
                                  Delete
                                </button>
                                <button
                                  className="block w-full text-left text-sm px-4 py-1 mt-1"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopyMessage(m.message);
                                    setOpenMenuIndex(null);
                                  }}
                                >
                                  Copy
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      }
                    })
                  )}
                  {isTyping && (
                    <div className="text-sm text-gray-400 italic ml-4">
                      AI is typing...
                    </div>
                  )}
                  {needsHumanAttention && (
                    <div className="flex items-center gap-2 text-yellow-500 text-sm ml-4">
                      <AlertTriangle size={16} />
                      <span>This conversation may need human attention</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Message input */}
              <div className="space-y-3">
                {/* File preview area */}
                {selectedFiles.length > 0 && (
                  <div className="px-3 py-2 bg-gray-100 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">Selected files ({selectedFiles.length})</span>
                      <button
                        onClick={() => setSelectedFiles([])}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm">
                          {getFileIcon(file.type)}
                          <span className="flex-1 truncate text-gray-600">{file.name}</span>
                          <span className="text-gray-500 text-xs">{formatFileSize(file.size)}</span>
                          <button
                            onClick={() => removeSelectedFile(index)}
                            className="text-red-500 hover:text-red-400"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                    {isUploading && (
                      <div className="mt-2">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-500 mt-1">Uploading... {uploadProgress}%</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex p-2 justify-between items-center w-full gap-2">
                  <div className="flex-1 flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                      accept="image/*,video/*,application/pdf,.doc,.docx,.txt,.zip,.rar"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
                      disabled={isUploading}
                      title="Attach files"
                    >
                      <Paperclip className="w-5 h-5" />
                    </button>
                    <div className="border h-[40px] p-0 flex-1 rounded-full relative">
                      <input
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyPress={handleKeyPress}
                        type="text"
                        placeholder="Type a message or attach files..."
                        className="w-full rounded-full h-full outline-none p-3 px-5 bg-gray-100 text-gray-900"
                        disabled={isUploading}
                      />
                    </div>
                  </div>
                  <div className="w-[40px] p-2 flex justify-center items-center rounded-full">
                    <Button
                      variant="ghost"
                      className="hover:bg-transparent"
                      onClick={send}
                      disabled={isUploading || (!text.trim() && selectedFiles.length === 0)}
                    >
                      <Send className="text-gray-700" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div
              onClick={() => setShowLeftSidebar(true)}
              className="w-full flex justify-center items-center font-bold text-gray-900 h-[400px]"
            >
              <p className="text-muted-foreground text-sm flex items-center">
                Select a seller to continue
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Fullscreen Image Modal */}
      {showFullscreenModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={closeFullscreenModal}
        >
          <div className="relative max-w-full max-h-full">
            <img
              src={fullscreenImage}
              alt="Fullscreen view"
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={closeFullscreenModal}
              className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-opacity"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chat;
